import { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import { Save, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const KINDS = [
  { key: "order",   label: "Order Number",       hint: "Applied to sales-order numbers generated on cart checkout." },
  { key: "invoice", label: "Invoice Number",     hint: "Sequence used on Tax Invoice PDFs. Format becomes <PREFIX>/FY/N<SUFFIX>." },
  { key: "po",      label: "Purchase Order",     hint: "Sequence used on supplier PO PDFs." },
  { key: "challan", label: "Delivery Challan",   hint: "Sequence used on Delivery Challan PDFs." },
];

export function DocNumberSettingsPanel() {
  const [fy, setFy] = useState("");
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);

  const load = () => {
    api.get("/admin/doc-number-settings")
      .then(r => { setSettings(r.data.settings); setFy(r.data.current_fy); })
      .catch(() => toast.error("Failed to load numbering settings"));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/doc-number-settings", settings);
      toast.success("Numbering settings saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const patchKind = (kind, field, value) => {
    setSettings(s => ({ ...s, [kind]: { ...(s?.[kind] || { prefix: "", suffix: "" }), [field]: value } }));
  };

  const downloadFyZip = async () => {
    setZipBusy(true);
    try {
      const res = await api.get("/admin/documents/current-fy.zip", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `TileRoom_FY${fy}_documents.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No documents for the current FY yet");
    } finally { setZipBusy(false); }
  };

  if (!settings) return null;

  return (
    <div className="mt-14 border-t border-[#1A1A1A]/10 pt-12" data-testid="doc-number-settings-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <p className="label-eyebrow">Sequential Documents</p>
          <h3 className="font-editorial text-3xl tracking-editorial mt-2">Prefix &amp; Suffix Settings</h3>
          <p className="text-xs text-[#8A8A8A] mt-2 max-w-2xl">
            Configure the prefix and (optional) suffix used for Invoice, PO, Challan and Order numbers.
            Numbers roll over automatically on 1 April every year. Current FY: <strong className="text-[#1E2C5A]">{fy}</strong>.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1 bg-[#1E2C5A] text-[#C9A968] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] border border-[#C9A968]/40" data-testid="fy-badge">
            FY {fy}
          </span>
          <button
            onClick={downloadFyZip}
            disabled={zipBusy}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em] border border-[#C9A968] text-[#C9A968] hover:bg-[#C9A968] hover:text-white transition-colors disabled:opacity-50"
            data-testid="download-fy-zip"
            title="Bundle every invoice + PO + challan for the current FY into one ZIP"
          >
            <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
            {zipBusy ? "Preparing…" : `Export FY ${fy} ZIP`}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {KINDS.map(k => (
          <div key={k.key} className="border border-[#1A1A1A]/15 bg-white p-5" data-testid={`doc-cfg-${k.key}`}>
            <p className="font-medium text-sm text-[#1E2C5A]">{k.label}</p>
            <p className="text-[11px] text-[#8A8A8A] mt-1 mb-3">{k.hint}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Prefix</Label>
                <Input
                  className="rounded-none font-mono"
                  value={settings[k.key]?.prefix || ""}
                  onChange={(e) => patchKind(k.key, "prefix", e.target.value)}
                  data-testid={`prefix-${k.key}`}
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Suffix (optional)</Label>
                <Input
                  className="rounded-none font-mono"
                  value={settings[k.key]?.suffix || ""}
                  onChange={(e) => patchKind(k.key, "suffix", e.target.value)}
                  placeholder="e.g. /GST"
                  data-testid={`suffix-${k.key}`}
                />
              </div>
            </div>
            <p className="text-[11px] text-[#5C5C5C] mt-3 font-mono" data-testid={`preview-${k.key}`}>
              Preview: <span className="text-[#1E2C5A]">
                {k.key === "order"
                  ? `${settings[k.key]?.prefix || ""}${new Date().toISOString().slice(0,7).replace('-','')}1001${settings[k.key]?.suffix || ""}`
                  : `${settings[k.key]?.prefix || ""}/${fy}/1${settings[k.key]?.suffix || ""}`}
              </span>
            </p>
          </div>
        ))}
      </div>

      <Button onClick={save} disabled={busy} className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45] mt-6" data-testid="save-doc-settings">
        <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
        {busy ? "Saving…" : "Save Numbering Settings"}
      </Button>
    </div>
  );
}
